import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import SEOPageLayout from '../components/seo/SEOPageLayout';

const relatedLinks = [
  { to: '/entreprise-interrogeable', label: 'Et si votre entreprise devenait interrogeable ?' },
  { to: '/clone-collaborateur-entreprise', label: 'Cloner la mémoire d\'un collaborateur' },
  { to: '/productivite-equipe-outil', label: 'Comment vos équipes perdent 30% de leur temps' },
];

const MemoireEntreprise = () => {
  return (
    <SEOPageLayout
      title="La mémoire d'entreprise : une nouvelle façon d'accéder à l'information"
      metaTitle="Mémoire d'entreprise et futur du travail | Cloneia"
      metaDescription="Aujourd'hui, on cherche l'information. Demain, on la demande. Découvrez comment la mémoire d'entreprise transforme le futur du travail."
      url="/memoire-entreprise-futur-travail"
      readingTime={4}
      subtitle="Aujourd'hui, on cherche l'information. Demain, on la demande."
      relatedLinks={relatedLinks}
    >
      <article className="space-y-12 text-[#a1a1aa] leading-relaxed" data-testid="seo-article-memoire">

        {/* Section 1 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le travail a changé. Pas l'accès à l'information.
          </h2>
          <p>
            En vingt ans, tout a évolué dans l'entreprise. Les outils, les process, la communication. Mais une chose n'a pas bougé : la façon dont on accède à l'information.
          </p>
          <p className="mt-4">
            On cherche. On fouille. On demande. On attend.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            C'est un modèle qui a atteint ses limites.
          </p>
        </motion.div>

        {/* Section 2 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le modèle actuel : chercher
          </h2>
          <p>
            Aujourd'hui, pour obtenir une information en entreprise, il faut savoir où elle se trouve. Et souvent, qui la détient.
          </p>
          <p className="mt-4">
            C'est un modèle basé sur la recherche. On navigue entre les outils, on interroge les collègues, on reconstitue les pièces du puzzle.
          </p>
          <p className="mt-4">
            Ce modèle fonctionne — tant que l'entreprise est petite, que les équipes sont stables, que l'information est simple.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Dès que l'entreprise grandit, il s'effondre.
          </p>
        </motion.div>

        {/* Section 3 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Le nouveau modèle : demander
          </h2>
          <p className="text-[#f5f5f5] font-medium text-lg">
            Pour la première fois, l'information de l'entreprise devient directement accessible.
          </p>
          <p className="mt-4">
            Pas en cherchant dans un outil. Pas en demandant à quelqu'un. En posant simplement la question.
          </p>
          <p className="mt-4">
            C'est un changement fondamental. On passe d'un modèle où il faut savoir chercher, à un modèle où il suffit de demander.
          </p>
        </motion.div>

        {/* Section 4 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            La collaboration augmentée
          </h2>
          <p>
            Ce nouveau modèle ne remplace pas la collaboration humaine. Il l'augmente.
          </p>
          <p className="mt-4">
            Quand l'accès à l'information est instantané, les échanges entre collègues changent de nature. On ne se contacte plus pour demander un historique. On se contacte pour décider, créer, avancer.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Moins de temps à chercher. Plus de temps à agir.
          </p>
        </motion.div>

        {/* Section 5 - Cloneia */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Comment Cloneia rend cela possible
          </h2>
          <p>
            Cloneia crée un clone mémoire de chaque collaborateur. Toute l'information de ses échanges est captée automatiquement, sans saisie, sans effort.
          </p>
          <p className="mt-4">
            Cette mémoire devient accessible à tous, à tout moment. Par une simple question.
          </p>
          <div className="mt-6 p-6 rounded-xl border border-[#3b82f6]/20 bg-[#1a1a2e]/50">
            <p className="text-[#f5f5f5] text-lg font-medium">
              Le système est invisible. La connaissance, elle, ne l'est plus.
            </p>
          </div>
        </motion.div>

        {/* Section 6 */}
        <motion.div {...fadeInUp}>
          <h2 className="text-2xl font-semibold text-[#f5f5f5] mb-4">
            Une nouvelle catégorie émerge
          </h2>
          <p>
            Ce que propose Cloneia n'est pas une amélioration d'un outil existant. C'est une nouvelle approche.
          </p>
          <p className="mt-4">
            Un système qui rend la mémoire de l'entreprise accessible. Qui transforme des années d'échanges en connaissance interrogeable. Qui libère les équipes de la recherche permanente.
          </p>
          <p className="mt-4 text-[#f5f5f5] font-medium">
            Une nouvelle façon de travailler. Où l'information vient à vous.
          </p>
        </motion.div>

      </article>
    </SEOPageLayout>
  );
};

export default MemoireEntreprise;
